<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Corretores</h1>
      <v-btn color="primary" @click="handleCreateBroker">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Corretor
      </v-btn>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchTerm"
              placeholder="Nome"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
        </v-row>

        <div style="max-width: 100%; overflow-x: auto;">
          <v-data-table-server
            :headers="columns"
            :items="filteredBrokers"
            :items-per-page="10"
            :items-length="filteredBrokers.length"
            :loading="isLoading"
            :no-data-text="noDataMessage"
            class="elevation-0"
            style="min-width: 1000px;"
          >
            <template v-slot:item.name="{ item }">
              <span>{{ item.name }}</span>
            </template>
            <template v-slot:item.creci="{ item }">
              <span>{{ item.creci }}</span>
            </template>
            <template v-slot:item.email="{ item }">
              <span>{{ item.email }}</span>
            </template>
            <template v-slot:item.phone="{ item }">
              <span>{{ formatPhone(item.phone) }}</span>
            </template>
            <template v-slot:item.is_plantao="{ item }">
              <v-chip :color="plantaoTagMap[item.is_plantao]" label>
                {{ formatIsPlantao(item.is_plantao) }}
              </v-chip>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip :color="isActiveTagMap[item.is_active]" label>
                {{ formatIsActive(item.is_active) }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                icon="mdi-pencil"
                color="warning"
                class="mr-2" size="large"
                @click="handleEdit(item)"
                title="Editar"
              />
              <v-icon
                icon="mdi-lock-reset"
                color="info" size="large"
                @click="handleResetPassword(item)"
                title="Trocar Senha"
              />
            </template>
          </v-data-table-server>
        </div>

        <v-alert
          v-if="hasError && !isLoading"
          type="error"
          variant="tonal"
          class="mt-4 text-center"
        >
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- Modal de senha -->
  <v-dialog v-model="showPasswordModal" max-width="500px">
    <v-card>
      <v-card-title class="headline text-h6 font-weight-bold text-grey-darken-4">
        Trocar Senha
      </v-card-title>
      <v-card-text>
        <v-form ref="passwordForm">
          <v-text-field
            v-model="newPassword"
            label="Nova Senha"
            type="password"
            :rules="[rules.required, rules.min]"
            required
          />
          <v-text-field
            v-model="confirmPassword"
            label="Confirmar Nova Senha"
            type="password"
            :rules="[rules.required, passwordMatch]"
            required
          />
        </v-form>
      </v-card-text>
      <v-card-actions class="d-flex justify-space-between">
        <v-btn color="error" variant="text" @click="closePasswordModal">Cancelar</v-btn>
        <v-btn color="primary" variant="flat" @click="resetPassword">Salvar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingOverlay } from '@/store/loadingOverlay';

const router = useRouter();
const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();

const brokers = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const searchTerm = ref('');

const showPasswordModal = ref(false);
const newPassword = ref('');
const confirmPassword = ref('');
const selectedBroker = ref(null);

const passwordForm = ref(null);

const passwordMatch = computed(() => {
  return newPassword.value === confirmPassword.value || 'As senhas não coincidem.';
});

const rules = {
  required: value => !!value || 'Campo obrigatório.',
  min: v => v.length >= 6 || 'A senha deve ter no mínimo 6 caracteres.',
};

const plantaoTagMap = {
  0: 'error',
  1: 'success',
};

const isActiveTagMap = {
  0: 'error',
  1: 'success',
};

const filteredBrokers = computed(() => {
  let result = brokers.value;

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(
      (broker) =>
        broker.name.toLowerCase().includes(term)
    );
  }

  return result;
});

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...';
  if (filteredBrokers.value.length === 0) return 'Nenhum corretor encontrado.';
  return '';
});

const columns = [
  { title: 'Nome', key: 'name', sortable: true },
  { title: 'CRECI', key: 'creci', sortable: true },
  { title: 'Telefone', key: 'phone', sortable: true },
  { title: 'Plantão', key: 'is_plantao', sortable: true },
  { title: 'Situação', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false },
];

const loadBrokers = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadingOverlay.startLoading();
  try {
    const response = await api.get('/brokers', {
      params: {
        role: ['broker', 'adminbroker']
      }
    });

    brokers.value = response.data.data;
  } catch (error) {
    console.error('Erro ao carregar corretores:', error);
    hasError.value = true;
    toastStore.showToast({
      message: error?.response?.data?.error || 'Ocorreu um erro ao carregar a lista de corretores.',
      color: 'error',
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

const handleCreateBroker = () => {
  router.push('/app/corretores/novo');
};

const handleEdit = async (row) => {
  router.push({ name: 'broker-edit', params: { id: row.id } });
};

const handleResetPassword = (item) => {
  selectedBroker.value = item;
  showPasswordModal.value = true;
};

const closePasswordModal = () => {
  showPasswordModal.value = false;
  newPassword.value = '';
  confirmPassword.value = '';
  selectedBroker.value = null;
  passwordForm.value.resetValidation();
};

const resetPassword = async () => {
  const { valid } = await passwordForm.value.validate();
  if (!valid) {
    return;
  }

  loadingOverlay.startLoading();
  try {
    const response = await api.put(`/brokers/${selectedBroker.value.id}/password`, {
      password: newPassword.value,
    });

    if (response.status === 200) {
      toastStore.showToast({
        message: 'Senha alterada com sucesso!',
        color: 'success',
      });
      closePasswordModal();
    } else {
      toastStore.showToast({
        message: 'Ocorreu um erro ao tentar alterar a senha.',
        color: 'error',
      });
    }
  } catch (error) {
    console.error('Erro ao trocar a senha:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Ocorreu um erro ao trocar a senha.',
      color: 'error',
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};

function formatIsPlantao(is_plantao) {
  switch (is_plantao) {
    case 0:
      return 'Não';
    case 1:
      return 'Sim';
    default:
      return is_plantao;
  }
}

function formatIsActive(is_active) {
  switch (is_active) {
    case 0:
      return 'Desativado';
    case 1:
      return 'Ativo';
    default:
      return is_active;
  }
}

function formatPhone(phone) {
  if (!phone) {
    return '';
  }

  const cleaned = ('' + phone).replace(/\D/g, '');

  const match11 = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match11) {
    return `(${match11[1]}) ${match11[2]}-${match11[3]}`;
  }

  const match10 = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
  if (match10) {
    return `(${match10[1]}) ${match10[2]}-${match10[3]}`;
  }

  return phone;
}

onMounted(() => {
  loadBrokers();
});
</script>